/**
 * SuperAdminWipModulesPanel — Permet au SuperAdmin de marquer des modules comme
 * "🚧 En développement" (WIP). Quand un module est WIP :
 *   • Côté UI client (Admin/Manager/Employé) → item de sidebar grisé avec badge "BIENTÔT"
 *   • Côté SuperAdmin → accès normal (pour finir le développement / tester)
 *
 * Endpoints :
 *   GET  /api/superadmin/modules/wip
 *   PUT  /api/superadmin/modules/{id}/wip
 */
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, RefreshCw, Wrench, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { invalidateWipCache } from '../hooks/useWipModules';

export default function SuperAdminWipModulesPanel() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [editingMessage, setEditingMessage] = useState({}); // {moduleId: "msg"}

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/superadmin/modules/wip');
      setModules(data?.modules || []);
    } catch (err) {
      toast.error("Impossible de charger l'état des modules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (moduleId, currentValue) => {
    const newValue = !currentValue;
    setToggling(moduleId);
    try {
      const message = editingMessage[moduleId] || undefined;
      const { data } = await api.put(`/superadmin/modules/${moduleId}/wip`, {
        isWip: newValue,
        message,
      });
      toast.success(data?.message || 'Module mis à jour');
      // Optimistic update
      setModules(prev => prev.map(m =>
        m.id === moduleId ? { ...m, isWip: newValue, message: message ?? m.message } : m
      ));
      // Invalider le cache du hook pour propager aux autres composants (sidebar)
      invalidateWipCache();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setToggling(null);
    }
  };

  const wipCount = modules.filter(m => m.isWip).length;

  return (
    <Card data-testid="wip-modules-panel" className="mb-8 border-amber-200/60 bg-gradient-to-br from-amber-50/40 to-white">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-amber-100">
              <Wrench className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                Modules en développement
                {wipCount > 0 && (
                  <Badge className="bg-amber-100 text-amber-700 border border-amber-200">
                    {wipCount} en cours
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-1">
                Désactive un module pour le griser dans la sidebar des clients (Admin / Manager / Employé).
                Les SuperAdmin gardent un accès normal pour finir le développement.
              </CardDescription>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
            data-testid="wip-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="py-8 flex items-center justify-center text-slate-500 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : modules.length === 0 ? (
          <div className="py-8 text-center text-slate-500 text-sm">Aucun module défini.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {modules.map((m) => (
              <div key={m.id}
                data-testid={`wip-module-${m.id}`}
                className={`flex items-start gap-3 rounded-xl border p-3 transition
                  ${m.isWip
                    ? 'border-amber-300 bg-amber-50/60'
                    : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 truncate">{m.name}</span>
                    {m.isWip && (
                      <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-[10px] font-bold tracking-wider">
                        🚧 BIENTÔT
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    id: <code className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">{m.id}</code>
                    {m.updatedAt && (
                      <span className="ml-2">
                        Mis à jour le {new Date(m.updatedAt).toLocaleDateString('fr-FR')} par {m.updatedBy || 'inconnu'}
                      </span>
                    )}
                  </div>
                  {/* Message custom (visible côté UI sur la page WIP) */}
                  <div className="mt-2">
                    <Input
                      data-testid={`wip-message-${m.id}`}
                      type="text"
                      placeholder="Message custom (optionnel) — ex: « Sortie prévue fin juin »"
                      value={editingMessage[m.id] !== undefined ? editingMessage[m.id] : (m.message || '')}
                      onChange={(e) => setEditingMessage(prev => ({ ...prev, [m.id]: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1.5 pt-1">
                  <Switch
                    data-testid={`wip-toggle-${m.id}`}
                    checked={m.isWip}
                    onCheckedChange={() => handleToggle(m.id, m.isWip)}
                    disabled={toggling === m.id}
                  />
                  <span className="text-[10px] text-slate-500">
                    {m.isWip ? 'WIP' : 'Actif'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {wipCount > 0 && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p>
              <strong>{wipCount} module{wipCount > 1 ? 's sont' : ' est'} en développement.</strong>{' '}
              Les clients (Admin / Manager / Employé) verront ces modules grisés dans leur sidebar avec un badge "BIENTÔT".
              Tu peux continuer à les tester normalement depuis ton compte SuperAdmin.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
